import {
  CXoneAuthByToken,
  AgentSessionManager,
  CXoneAuthByCode,
  AdminService,
  AgentSettings,
  BusinessUnit,
  Permissions,
  HttpResponse,
  CXoneAuthBase,
  CentralBrandingProfile,
  LocalStorageHelper,
  StorageKeys,
  BrandingProfile
} from '@nice-ccf/core-sdk';
import {
  AgentState,
  AuthSettings,
  AuthToken,
  AuthWithCodeReq,
  AuthWithTokenReq,
  StartSessionRequest,
} from '@nice-ccf/common-sdk';
import { AgentStateService } from '@nice-ccf/agent-sdk';

/**
 * Class to manage authentication mechanism
 */
export class CXoneAuth {
  private static singleton: CXoneAuth;
  private cxoneAuthByCode!: CXoneAuthByCode;
  private cxoneAuthByToken!: CXoneAuthByToken;
  private agentSession!: AgentSessionManager;
  private adminService!: AdminService;
  public agentStateService!: AgentStateService;

  /**
   * Method to create singleton object of the class
   * ```
   * @example
   * const authObject = CXoneAuth.instance();
   * ```
   */
  public static get instance(): CXoneAuth {
    if (!CXoneAuth.singleton) {
      CXoneAuth.singleton = new CXoneAuth();
    }
    return CXoneAuth.singleton;
  }

  /**
   * Used to start the check for refresh token
   * Here, first we will initiate the worker which have a timeout, this timeout will get triggered based on the expiry time of the token we have passed
   * Once the expiry time is reached the worker will execute the callback to get the refresh token that we have passed
   * @param authToken - token object which will have refresh token and the expire in detail required to obtain the new token
   * @example - startRefreshTokenCheck(authToken)
   */
  startRefreshToken(authToken: AuthToken) {
    CXoneAuthBase.Instance.startRefreshTokenCheck(authToken);
  }

  /**
   *
   * @param authSettings - initialize the auth settings
   * ```
   * @example
   * init({'cxoneHostname': 'https://cxone.dev.niceincontact.com', 'clientId': 'Salesforce Agent Console@inContact Inc.'})
   * ```
   */
  init(authSettings: AuthSettings) {
    this.cxoneAuthByCode = CXoneAuthByCode.instance;
    this.cxoneAuthByCode.init(authSettings);
  }

  /**
   * Method generate the Authorize url using authorize endpoint with clientId, code challenge, authMode and codeChallengeMethod.
   * This url will be use to open the login screen in page or popup window based on the display value.
   *
   * @param displayMode - get the authmode, whether page or popup
   * @param codeChallengeMethod - 'S256'
   * @returns authUrl
   * ```
   * @example
   * getAuthorizeUrl('page', 'S256');
   * ```
   *
   */
  getAuthorizeUrl(
    displayMode: string,
    codeChallengeMethod: string
  ): Promise<string> {
    return this.cxoneAuthByCode.getAuthorizeUrl(
      displayMode,
      codeChallengeMethod
    );
  }

  /**
   * Method to generate the access token and call the getCXoneConfiguration method in the response
   * to get the api endpoint url from the cxone configuration.
   *
   * @param clientId - 'client id'
   * @param code - 'generated code after authenticate'
   * ```
   * @example
   * getAccessToken('Salesforce Agent Cons..', 'eyJ0eXAiOiJKV1Qi...')
   * ```
   */
  getAccessToken(
    authenticate: AuthWithTokenReq | AuthWithCodeReq
  ): Promise<string | AuthToken> {
    if ('accessToken' in authenticate) {
      this.cxoneAuthByToken = CXoneAuthByToken.instance;
      return this.cxoneAuthByToken.authenticate(
        authenticate as AuthWithTokenReq
      );
    } else {
      this.cxoneAuthByCode = CXoneAuthByCode.instance;
      return this.cxoneAuthByCode.getAccessToken(
        authenticate as AuthWithCodeReq
      );
    }
  }

  /**
   * Method to return agent permissions
   * @returns - return the agent permissions
   * ```
   * @example
   * getPermissions()
   * ```
   */
  getPermissions(): Promise<Permissions[]> {
    if (!this.adminService) {
      this.adminService = AdminService.instance;
    }
    return this.adminService.getPermissions();
  }

  /**
   * Method to return agent settings
   * @returns - return the agent settings
   * ```
   * @example
   * getAgentSettings()
   * ```
   */
  getAgentSettings(): Promise<AgentSettings> {
    if (!this.adminService) {
      this.adminService = AdminService.instance;
    }
    return this.adminService.getAgentSettings();
  }

  /**
   * Method to return business unit features
   * @returns - returns the business unit features
   * ```
   * @example
   * getBusinessUnit()
   * ```
   */
  getBusinessUnit(): Promise<BusinessUnit> {
    if (!this.adminService) {
      this.adminService = AdminService.instance;
    }
    return this.adminService.getBusinessUnit();
  }

  /**
   * Method to return client data
   * @returns - returns the client data
   * ```
   * @example
   * getClientData()
   * ```
   */
  getClientData(): Promise<string> {
    if (!this.adminService) {
      this.adminService = AdminService.instance;
    }
    return this.adminService.getClientData();
  }

  /**
   * Method to return central branding profile
   * @returns - returns the central branding profile
   * ```
   * @example
   * getCentralBrandingProfile()
   * ```
   */
  getCentralBrandingProfile(): Promise<CentralBrandingProfile> {
    if (!this.adminService) {
      this.adminService = AdminService.instance;
    }
    return this.adminService.getCentralBrandingProfile();
  }

  /**
   * Method to return cxone branding profiles
   * @returns - returns the cxone branding profiles
   * ```
   * @example
   * getBrandingProfile()
   * ```
   */
   getBrandingProfile(): Promise<BrandingProfile> {
    if(!this.adminService){
      this.adminService = AdminService.instance;
    }
    return this.adminService.getBrandingProfile();
  }

  /**
   *
   * @param startSessionRequest - start agent session request object
   * ```
   * @example
   * const startSessionObj: StartSessionRequest = {
      stationId: stationId?.current?.value || '',
      stationPhoneNumber: stationPhoneNo?.current?.value || '',
    };
   * const startSession = this.startSession(startSessionObj);
   * ```
   */
  startSession(
    startSessionRequest: StartSessionRequest
  ): Promise<HttpResponse> {
    this.agentSession = AgentSessionManager.instance;
    if (!this.agentStateService) {
      this.agentStateService = AgentStateService.instance;
    }
    return this.agentSession.startSession(startSessionRequest);
  }

  /**
   * Method to join an existing agent session
   * ```
   * @example
   * const joinSession = this.joinSession()
   * ```
   */
  joinSession(): Promise<HttpResponse> {
    this.agentSession = AgentSessionManager.instance;
    if (!this.agentStateService) {
      this.agentStateService = AgentStateService.instance;
    }
    return this.agentSession.joinSession();
  }

  /**
   *
   * @param forceLogoff - boolean values forces agent to end session
   * @param endContacts - boolean value indicating if an existing contact should end
   * @param ignorePersonalQueue - boolean indicating the agent session to end, regardless of the personal queue
   * ```
   * @example
   * const endSession = this.endSession(true, false, false);
   * ```
   */
  endSession(
    forceLogoff: boolean,
    endContacts: boolean,
    ignorePersonalQueue: boolean
  ): Promise<HttpResponse> {
    this.agentSession = AgentSessionManager.instance;
    return this.agentSession.endSession(
      forceLogoff,
      endContacts,
      ignorePersonalQueue
    );
  }

  /**
   * Sets agent state
   * @param agentState - updated agent state value
   * @example
   * ```
   * const agentState = { state: 'Unavailable', reason:'Break' };
   * setAgentState(agentState)
   * ```
   */
  setAgentState(agentState: AgentState): Promise<HttpResponse> {
    return this.agentStateService.setAgentState(agentState);
  }

  /**
   * restores data and re-initiates auth flow
   * @returns - re-initialization status for auth flow
   * @example
   * ```
   * const isAuthRestored = this.restoreData();
   * ```
   */
   restoreData(): boolean {
    const userLoginDetails = CXoneAuthBase.getAuthState();
    if (userLoginDetails.isTokenValid) {
      if (userLoginDetails.authToken) {
        this.cxoneAuthByToken = CXoneAuthByToken.instance;
        CXoneAuthBase.Instance.setAuthDataAndTriggerRefreshFlow(userLoginDetails.authToken);
        const cxOneConfig = LocalStorageHelper.getItem(
          StorageKeys.CXONE_CONFIG,
          true
        );
        if (cxOneConfig) CXoneAuthBase.Instance.setCXoneConfig(cxOneConfig);
        const sessionId = LocalStorageHelper.getItem(
          StorageKeys.ACD_SESSION_ID
        );
        if (sessionId) {
          this.joinSession();
        }
        return true;
      }
    }
    return false;
  }

}
